import {
  BOOKING_AVAILABILITY_CLIENT_CACHE_MS,
  BOOKING_AVAILABILITY_PREFETCH_MIN_MS,
} from "@/lib/booking/booking-availability-cache";
import type { Lang } from "@/lib/types";

export type BookingSlotMap = Record<string, { slots: string[] }>;
export type BookingMonth = { year: number; monthIndex: number };

type CacheEntry = {
  expiresAt: number;
  promise: Promise<BookingSlotMap>;
};

const availabilityCache = new Map<string, CacheEntry>();
let lastPrefetchAt = 0;

function availabilityRequest(
  month: BookingMonth,
  timeZone: string,
  lang: Lang,
) {
  const startDate = Date.UTC(month.year, month.monthIndex, 1);
  const endDate = Date.UTC(month.year, month.monthIndex + 1, 1) - 1;
  const query = new URLSearchParams({
    startDate: String(startDate),
    endDate: String(endDate),
    timezone: timeZone,
    lang,
  });
  return `/api/booking/availability?${query}`;
}

export function removeSlotFromBookingSlotMap(
  slots: BookingSlotMap,
  slotIso: string,
): BookingSlotMap {
  let changed = false;
  const next: BookingSlotMap = {};

  for (const [dateKey, day] of Object.entries(slots)) {
    if (!day?.slots.length) continue;
    const filtered = day.slots.filter((slot) => slot !== slotIso);
    if (filtered.length === day.slots.length) {
      next[dateKey] = day;
      continue;
    }
    changed = true;
    if (filtered.length) next[dateKey] = { slots: filtered };
  }

  return changed ? next : slots;
}

export function invalidateBookingAvailabilityMonth(
  month: BookingMonth,
  timeZone: string,
  lang: Lang,
) {
  availabilityCache.delete(availabilityRequest(month, timeZone, lang));
}

type LoadBookingAvailabilityOptions = {
  /** Skip the in-memory cache (e.g. after a slot was taken). */
  bypassCache?: boolean;
};

export function loadBookingAvailability(
  month: BookingMonth,
  timeZone: string,
  lang: Lang,
  options?: LoadBookingAvailabilityOptions,
): Promise<BookingSlotMap> {
  const url = availabilityRequest(month, timeZone, lang);
  const now = Date.now();
  if (!options?.bypassCache) {
    const cached = availabilityCache.get(url);
    if (cached && cached.expiresAt > now) return cached.promise;
  }

  const promise = fetch(url)
    .then(async (response) => {
      if (!response.ok) throw new Error("Availability request failed");
      const payload = (await response.json()) as { slots?: BookingSlotMap };
      return payload.slots ?? {};
    })
    .catch((error) => {
      availabilityCache.delete(url);
      throw error;
    });
  availabilityCache.set(url, {
    expiresAt: now + BOOKING_AVAILABILITY_CLIENT_CACHE_MS,
    promise,
  });
  return promise;
}

export function prefetchBookingAvailability(lang: Lang = "en") {
  const now = Date.now();
  if (now - lastPrefetchAt < BOOKING_AVAILABILITY_PREFETCH_MIN_MS) return;
  lastPrefetchAt = now;

  const timeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Stockholm";
  const current = new Date();
  void loadBookingAvailability(
    { year: current.getFullYear(), monthIndex: current.getMonth() },
    timeZone,
    lang,
  ).catch(() => undefined);
}

export function clearBookingAvailabilityCache() {
  availabilityCache.clear();
  lastPrefetchAt = 0;
}
